import { Feature, Point } from 'geojson';
import { ExpressionSpecification, MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import { ComponentProps, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { useClientNavigationPushState } from '@alltrails/core/hooks/useClientNavigation';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { COLOR_NEUTRAL_300, COLOR_NEUTRAL_700_A } from '@alltrails/denali/tokens';
import { type POISearchResult, poiSubTypes } from '@alltrails/types/AlgoliaResults';
import { useDelayedOnStyleChange } from '../../../hooks/useDelayedOnStyleChange';
import useMap from '../../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { updateActivePOI, updateCursor, updateHoveredPOI, updateRenderedPOIs } from '../../../redux/reducer';
import { defaultPOIPinLayerPaintProps, getLayerBeforeId, poiPinsId } from '../../../utils/layers';
import { serializedCoordinatesToCoordinates } from '../../../utils/serializedCoordinates';
import createPOIIconPinSvg, { imageNamePrefix as iconPrefix } from '../../../utils/svgDefinitions/createPOIIconPinSvg';
import useSvgImages, { SvgDefinition } from '../../../utils/useSvgImages';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import { defaultPOIPinXSLayoutProps } from './constants';
import HoveredPOIPin from './POIPopupPin';
import type POIResultProperties from './POIResultProperties';

function poiToPOIFeature(serializedCoordinates: string, poi: POISearchResult): Feature<Point, POIResultProperties> {
  const { lat, lng } = serializedCoordinatesToCoordinates(serializedCoordinates);
  return {
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {
      hasPhotos: !!poi.photos_count,
      id: poi.ID,
      name: poi.name,
      popularity: poi.popularity ?? 0,
      serializedCoordinates,
      type: poi.subtype
    },
    type: 'Feature'
  };
}

export const poiSourceId = 'poi-results-source';

type POIResultsProps = {
  getPOIUrl?: (poi: POISearchResult) => string;
  onPOIClick?: (poi: POISearchResult) => void;
  onPOICardViewed?: ComponentProps<typeof HoveredPOIPin>['onPOICardViewed'];
};

const POIResults = ({ getPOIUrl, onPOIClick, onPOICardViewed }: POIResultsProps) => {
  const poiImageFetched = useRef<Set<number>>(new Set());
  const newFeatureSet = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const pushState = useClientNavigationPushState();
  const map = useMap();
  const isMobile = useIsMobileSizedScreen();
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const { activePOI, hoveredPOI, interactionBehavior, poiResults } = useAppSelector(state => ({
    activePOI: state.map.activePOI,
    hoveredPOI: state.map.hoveredPOI,
    interactionBehavior: state.map.poiInteractionBehavior,
    poiResults: state.map.poiResults
  }));

  useEffect(() => {
    newFeatureSet.current = true;
  }, [poiResults]);

  const features = useMemo(() => {
    if (!poiResults) {
      return [];
    }

    const poiFeatures: Feature<Point, POIResultProperties>[] = [];
    for (const [serializedCoordinates, poi] of Object.entries(poiResults)) {
      if (!poi?._geoloc || (!poi.photos_count && map && map.getZoom() < 8)) {
        continue;
      }
      poiFeatures.push(poiToPOIFeature(serializedCoordinates, poi));
    }
    return poiFeatures;
  }, [map, poiResults]);

  const defaultSvgDefinitions = useMemo(() => {
    const definitions: SvgDefinition[] = poiSubTypes.reduce((defs, poiType) => {
      defs.push(createPOIIconPinSvg(poiType, 'xs', COLOR_NEUTRAL_300, COLOR_NEUTRAL_700_A));
      if (interactionBehavior === 'enlarge') {
        defs.push(createPOIIconPinSvg(poiType, 'md', COLOR_NEUTRAL_300, COLOR_NEUTRAL_700_A));
      }
      return defs;
    }, [] as SvgDefinition[]);
    return definitions;
  }, [interactionBehavior]);
  const { imagesAreLoaded } = useSvgImages(defaultSvgDefinitions);

  const onHover = useCallback(
    (properties?: POIResultProperties & { cluster_id?: number }) => {
      if (properties) {
        const serializedCoordinates = properties.serializedCoordinates;
        dispatch(updateHoveredPOI(poiResults?.[serializedCoordinates]));
        dispatch(updateCursor('pointer'));
      }
    },
    [dispatch, poiResults]
  );

  const onHoverEnd = useCallback(() => {
    dispatch(updateHoveredPOI(undefined));
    dispatch(updateCursor(''));
  }, [dispatch]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true /* isMouseMove */, 50);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (onMouseEnter && event.features) {
        const marker = event.features[0];
        onMouseEnter(marker.properties as (POIResultProperties & { cluster_id?: number }) | undefined);
      }
    },
    [onMouseEnter]
  );

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (event.features) {
        event.preventDefault(); // Allows us to detect when a user clicks on the map but NOT on this layer
        const marker = event.features[0];
        if (poiResults && marker.properties) {
          const properties = marker.properties as { cluster?: boolean; serializedCoordinates: string };
          const { cluster, serializedCoordinates } = properties;
          const geometry = marker.geometry;
          const clusterCoordinates =
            cluster && geometry.type === 'Point' ? { lat: geometry.coordinates[1], lng: geometry.coordinates[0] } : undefined;
          const poi = poiResults[serializedCoordinates];
          onPOIClick?.(poi);
          if (poi.ID === activePOI?.ID) {
            // if the already active POI is clicked, remove the active state
            dispatch(updateActivePOI(undefined));
            dispatch(updateHoveredPOI(undefined));
            dispatch(updateCursor(''));
          } else {
            dispatch(updateActivePOI({ ...poi, clusterCoordinates }));
          }
          if (getPOIUrl) {
            pushState(getPOIUrl(poi));
          }
        }
      }
    },
    [activePOI, dispatch, getPOIUrl, onPOIClick, poiResults, pushState]
  );

  const onStyleChange = useCallback(() => {
    poiImageFetched.current = new Set();
  }, []);
  useDelayedOnStyleChange(onStyleChange); // reload dynamic image pins when map style changes

  useEffect(() => {
    registerEventHandler(poiPinsId, 'click', onClick);
    if (!isMobile) {
      registerEventHandler(poiPinsId, 'mousemove', onMouseMove);
      if (onMouseLeave) {
        registerEventHandler(poiPinsId, 'mouseleave', onMouseLeave);
      }
    }

    return () => {
      unregisterEventHandler(poiPinsId, 'click', onClick);
      unregisterEventHandler(poiPinsId, 'mousemove', onMouseMove);
      if (onMouseLeave) {
        unregisterEventHandler(poiPinsId, 'mouseleave', onMouseLeave);
      }
    };
  }, [isMobile, onClick, onMouseEnter, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  useEffect(() => {
    if (!map) {
      return;
    }
    const onIdle = () => {
      if (newFeatureSet.current) {
        const renderedFeatures = map.queryRenderedFeatures({ layers: [poiPinsId] });
        dispatch(updateRenderedPOIs(renderedFeatures.length));
        newFeatureSet.current = false;
      }
    };

    map.on('idle', onIdle);
    return () => {
      map.off('idle', onIdle);
    };
  }, [dispatch, map]);

  useEffect(() => {
    const removeActiveStatesOnClick = (event: MapMouseEvent | MapTouchEvent) => {
      // We prevent default when a user clicks on this layer
      if (!event.defaultPrevented) {
        dispatch(updateActivePOI(undefined));
        dispatch(updateHoveredPOI(undefined));
      }
    };

    const removeActiveStatesOnMove = () => {
      dispatch(updateActivePOI(undefined));
      dispatch(updateHoveredPOI(undefined));
    };

    map?.on('click', removeActiveStatesOnClick);
    map?.on('movestart', removeActiveStatesOnMove);
    return () => {
      map?.off('click', removeActiveStatesOnClick);
      map?.off('movestart', removeActiveStatesOnMove);
    };
  }, [dispatch, map]);

  if (!map || !imagesAreLoaded) {
    return null;
  }

  const iconPinExpression: ExpressionSpecification = [
    'concat',
    `${iconPrefix}-`,
    ['get', 'type'],
    [
      'case',
      [
        'all',
        ['==', interactionBehavior, 'enlarge'],
        ['any', ['==', ['get', 'id'], hoveredPOI?.ID ?? null], ['==', ['get', 'id'], activePOI?.ID ?? null]]
      ],
      '-md',
      '-xs'
    ]
  ];

  const poiLayerLayout = {
    ...defaultPOIPinXSLayoutProps,
    'icon-image': iconPinExpression,
    'icon-ignore-placement': false,
    'icon-allow-overlap': false,
    'symbol-sort-key': [
      'case',
      [
        'all',
        ['==', interactionBehavior, 'enlarge'],
        ['any', ['==', ['get', 'id'], hoveredPOI?.ID ?? null], ['==', ['get', 'id'], activePOI?.ID ?? null]]
      ],
      1,
      ['-', 100, ['get', 'popularity']]
    ] as ExpressionSpecification
  };

  return (
    <>
      {/* Initial source used to render POI results and clusters */}
      <Source id={poiSourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
        <Layer
          source={poiSourceId}
          id={poiPinsId}
          type="symbol"
          beforeId={getLayerBeforeId(map, poiPinsId)}
          layout={poiLayerLayout}
          paint={defaultPOIPinLayerPaintProps}
        />
      </Source>
      {interactionBehavior === 'popup' && (
        <HoveredPOIPin
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          getPOIUrl={getPOIUrl}
          onPOICardViewed={onPOICardViewed}
          onPopupClick={onPOIClick}
        />
      )}
    </>
  );
};

export default POIResults;
